
// OAuth utility functions for production implementation

interface OAuthConfig {
  clientId: string;
  redirectUri: string;
  scope: string;
}

export interface OAuthUserData {
  providerId: string;
  email: string;
  displayName?: string;
  photoURL?: string | null;
}

export interface OAuthResponse {
  success: boolean;
  provider: string;
  userData: OAuthUserData;
}

const OAUTH_CONFIGS: Record<string, OAuthConfig> = {
  google: {
    clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID || '',
    redirectUri: `${window.location.origin}/auth/callback/google`,
    scope: 'profile email'
  },
  facebook: {
    clientId: import.meta.env.VITE_FACEBOOK_APP_ID || '',
    redirectUri: `${window.location.origin}/auth/callback/facebook`,
    scope: 'public_profile,email'
  },
  apple: {
    clientId: import.meta.env.VITE_APPLE_CLIENT_ID || '',
    redirectUri: `${window.location.origin}/auth/callback/apple`,
    scope: 'name email'
  }
};

/**
 * Initiates OAuth flow for specified provider
 */
export const initiateOAuthFlow = (provider: 'google' | 'facebook' | 'apple') => {
  const config = OAUTH_CONFIGS[provider];
  
  if (!config.clientId) {
    // For testing purposes, provide a simulated OAuth flow when API keys are not set
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`${provider} client ID not configured. Using simulated OAuth flow for testing.`);
      window.location.href = `/auth/callback/${provider}?code=test-auth-code-${Date.now()}&state=test`;
      return;
    }
    throw new Error(`${provider} client ID not configured. Please add it to your environment variables.`);
  }
  
  // Add state parameter for CSRF protection
  const state = Math.random().toString(36).substring(2, 15);
  localStorage.setItem(`oauth_state_${provider}`, state);
  
  // Each provider has specific authentication flows
  switch (provider) {
    case 'google':
      // Google OAuth2 authorization flow
      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${config.clientId}&redirect_uri=${encodeURIComponent(config.redirectUri)}&response_type=code&scope=${encodeURIComponent(config.scope)}&state=${state}`;
      break;
      
    case 'facebook':
      // Facebook OAuth flow
      window.location.href = `https://www.facebook.com/v12.0/dialog/oauth?client_id=${config.clientId}&redirect_uri=${encodeURIComponent(config.redirectUri)}&scope=${encodeURIComponent(config.scope)}&state=${state}`;
      break;
      
    case 'apple':
      // Apple OAuth flow
      window.location.href = `https://appleid.apple.com/auth/authorize?client_id=${config.clientId}&redirect_uri=${encodeURIComponent(config.redirectUri)}&response_type=code&scope=${encodeURIComponent(config.scope)}&state=${state}`;
      break;
      
    default:
      throw new Error(`Unsupported provider: ${provider}`);
  }
};

/**
 * Handles OAuth callback
 * In production, this would exchange authorization code for tokens
 */
export const handleOAuthCallback = async (provider: string, code: string, state?: string): Promise<OAuthResponse> => {
  // Verify state parameter to prevent CSRF attacks
  if (state) {
    const savedState = localStorage.getItem(`oauth_state_${provider}`);
    if (savedState && savedState !== state && process.env.NODE_ENV === 'production') {
      throw new Error("Invalid state parameter. This could be a CSRF attack.");
    }
    // Clean up the state from localStorage
    localStorage.removeItem(`oauth_state_${provider}`);
  }
  
  // In test mode, we provide mock data
  if (code.startsWith('test-auth-code-') || process.env.NODE_ENV !== 'production') {
    console.log(`[TEST MODE] Processing ${provider} OAuth callback with code: ${code}`);
    
    return {
      success: true,
      provider,
      userData: {
        providerId: `${provider}-test-user-id`,
        email: `test-user@example.com`,
        displayName: `Test ${provider.charAt(0).toUpperCase() + provider.slice(1)} User`,
        photoURL: `https://randomuser.me/api/portraits/${Math.random() > 0.5 ? 'women' : 'men'}/${Math.floor(Math.random() * 100)}.jpg`
      }
    };
  }
  
  // In a real implementation, this would make a backend request to exchange the code for tokens
  console.log(`Processing ${provider} OAuth callback with code: ${code}`);
  
  try {
    // This would be an API call in production
    // For demo purposes, we're returning a mock response
    const response = await new Promise<OAuthResponse>(resolve => {
      setTimeout(() => {
        resolve({
          success: true,
          provider,
          userData: {
            providerId: `${provider}-user-id-${Math.random().toString(36).substring(2, 9)}`,
            email: `user-${Math.random().toString(36).substring(2, 9)}@example.com`,
            displayName: `${provider.charAt(0).toUpperCase() + provider.slice(1)} User`,
            photoURL: null
          }
        });
      }, 1000); // Simulate network delay
    });
    
    return response;
  } catch (error) {
    console.error(`Error processing ${provider} OAuth callback:`, error);
    throw error;
  }
};

/**
 * Validate an invite code against the server
 * In production, this would check the backend database
 */
export const validateInviteCode = async (code: string): Promise<boolean> => {
  // In a real implementation, this would verify the invite code with the backend
  console.log(`Validating invite code: ${code}`);
  
  // For demonstration, valid codes would be:
  // 1. Any code that starts with "HORMONY"
  // 2. For testing, "TEST123" is also valid
  const isValid = code.startsWith('HORMONY') || code === 'TEST123';
  
  return new Promise((resolve) => {
    // Simulate API call delay
    setTimeout(() => {
      resolve(isValid);
    }, 800);
  });
};

/**
 * Send an invite email to a user
 * In production, this would send an actual email
 */
export const sendInviteEmail = async (email: string, inviteCode: string): Promise<boolean> => {
  // In a real implementation, this would send an email via an API
  console.log(`Sending invite email to ${email} with code ${inviteCode}`);
  
  // Simulate sending an email
  return new Promise((resolve) => {
    // Simulate API call delay
    setTimeout(() => {
      // In production, this would check if the email was sent successfully
      const success = true;
      resolve(success);
    }, 1000);
  });
};

/**
 * Check if the current environment has OAuth client IDs configured
 * Useful for conditionally showing/hiding OAuth buttons when not configured
 */
export const isOAuthConfigured = (provider: 'google' | 'facebook' | 'apple'): boolean => {
  return !!OAUTH_CONFIGS[provider].clientId || process.env.NODE_ENV !== 'production';
};

/**
 * Get mock/test invite codes
 * In a real app, these would be generated by admins
 */
export const getTestInviteCodes = (): string[] => {
  if (process.env.NODE_ENV === 'production') {
    return [];
  }
  
  return [
    'HORMONY123',
    'HORMONYWELLNESS',
    'HORMONYBALANCE',
    'TEST123'
  ];
};
